import { useCallback, useEffect, useState, type RefObject } from "react";

import styled from "styled-components";

import { ChevronLeft, ChevronRight } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";

import { GROWTH_OVERVIEW_COLUMN_WIDTHS, GROWTH_OVERVIEW_QUERY_KEY } from "../const";

import { GROWTH_OVERVIEW_TABLE_Z_INDEX } from "./styled";

/** Matches grid min period width; columns only scroll when pinned at this size. */
const SCROLL_STEP_PX = GROWTH_OVERVIEW_COLUMN_WIDTHS.period;
const STICKY_COLUMNS_WIDTH = GROWTH_OVERVIEW_COLUMN_WIDTHS.name + GROWTH_OVERVIEW_COLUMN_WIDTHS.summary;

const GrowthOverviewScrollSteppersContainer = styled.div<{ $width: number }>`
    position: absolute;
    top: 0;
    left: ${STICKY_COLUMNS_WIDTH}px;
    width: ${({ $width }) => $width}px;
    height: 40px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.6s ease-in-out;
    z-index: ${GROWTH_OVERVIEW_TABLE_Z_INDEX.scrollSteppers};
    border-right: 1px solid ${colorsPalettes.carbon[100]};
`;

const GrowthOverviewScrollButton = styled.button<{ $side: "left" | "right"; $atEnd: boolean }>`
    position: absolute;
    top: -5px;
    ${({ $side }) => ($side === "left" ? "left: -20px;" : "right: -20px;")}
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 20px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.25);
    background-color: ${colorsPalettes.carbon[0]};
    cursor: pointer;
    opacity: ${({ $atEnd }) => ($atEnd ? 0.5 : 1)};
    transition: opacity 150ms;
    pointer-events: all;
    z-index: ${GROWTH_OVERVIEW_TABLE_Z_INDEX.scrollButton};

    path {
        fill: ${colorsPalettes.carbon[300]};
    }
`;

export const GrowthOverviewTableScrollWrapper = styled.div`
    position: relative;
    overflow: visible;

    &:hover ${GrowthOverviewScrollSteppersContainer} {
        opacity: 1;
    }
`;

interface IGrowthOverviewTableScrollSteppersProps {
    scrollAreaRef: RefObject<HTMLDivElement | null>;
    contentKey: string;
}

export const GrowthOverviewTableScrollSteppers = ({
    scrollAreaRef,
    contentKey,
}: IGrowthOverviewTableScrollSteppersProps) => {
    const [scrollContainerWidth, setScrollContainerWidth] = useState(0);
    const [hasOverflow, setHasOverflow] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollState = useCallback(() => {
        const element = scrollAreaRef.current;
        if (!element) {
            return;
        }

        const nextScrollContainerWidth = Math.max(element.clientWidth - STICKY_COLUMNS_WIDTH, 0);
        const overflow = element.scrollWidth > element.clientWidth;

        setScrollContainerWidth(nextScrollContainerWidth);
        setHasOverflow(overflow);
        setCanScrollLeft(element.scrollLeft > 0);
        setCanScrollRight(element.scrollLeft < element.scrollWidth - element.clientWidth - 1);
    }, [scrollAreaRef]);

    useEffect(() => {
        const element = scrollAreaRef.current;
        if (!element) {
            return;
        }

        updateScrollState();
        element.addEventListener("scroll", updateScrollState, { passive: true });

        const resizeObserver = new ResizeObserver(updateScrollState);
        resizeObserver.observe(element);

        return () => {
            element.removeEventListener("scroll", updateScrollState);
            resizeObserver.disconnect();
        };
    }, [scrollAreaRef, updateScrollState, contentKey]);

    const scrollBy = (pixels: number) => {
        scrollAreaRef.current?.scrollBy({ left: pixels, behavior: "smooth" });
    };

    if (!hasOverflow || scrollContainerWidth === 0) {
        return null;
    }

    return (
        <GrowthOverviewScrollSteppersContainer $width={scrollContainerWidth}>
            <GrowthOverviewScrollButton
                type="button"
                $side="left"
                $atEnd={!canScrollLeft}
                onClick={() => scrollBy(-SCROLL_STEP_PX)}
                data-automation={`${GROWTH_OVERVIEW_QUERY_KEY}-scroll-left`}
                aria-label="Scroll left"
            >
                <ChevronLeft size="xs" />
            </GrowthOverviewScrollButton>
            <GrowthOverviewScrollButton
                type="button"
                $side="right"
                $atEnd={!canScrollRight}
                onClick={() => scrollBy(SCROLL_STEP_PX)}
                data-automation={`${GROWTH_OVERVIEW_QUERY_KEY}-scroll-right`}
                aria-label="Scroll right"
            >
                <ChevronRight size="xs" />
            </GrowthOverviewScrollButton>
        </GrowthOverviewScrollSteppersContainer>
    );
};
